import { __decorate } from "tslib";
import React from 'react';
import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import isObject from 'lodash/isObject';
import defaultTo from 'lodash/defaultTo';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import KeyCode from 'choerodon-ui/lib/_util/KeyCode';
import { Size } from 'choerodon-ui/lib/_util/enum';
import DataSetComponent from '../data-set/DataSetComponent';
import ObserverSelect from '../select/Select';
import ObserverNumberField from '../number-field/NumberField';
import Button from '../button';
import autobind from '../_util/autobind';
import { $l } from '../locale-context';
import Pager from './Pager';
import Icon from '../icon';
import { QuickJumperPosition, SizeChangerPosition } from './enum';
import { ValueChangeAction } from '../text-field/enum';
import QuickJumper from './QuickJumper';
function defaultItemRender(page, type) {
    switch (type) {
        case 'first':
            return React.createElement(Icon, { type: "first_page" });
        case 'last':
            return React.createElement(Icon, { type: "last_page" });
        case 'prev':
            return React.createElement(Icon, { type: "navigate_before" });
        case 'next':
            return React.createElement(Icon, { type: "navigate_next" });
        case 'jump-prev':
        case 'jump-next':
            return '•••';
        default:
            return page;
    }
}
let Pagination = class Pagination extends DataSetComponent {
    get pageSize() {
        const { dataSet, pageSize } = this.observableProps;
        if (dataSet) {
            return dataSet.pageSize;
        }
        return pageSize;
    }
    get page() {
        const { dataSet, page } = this.observableProps;
        if (dataSet) {
            return dataSet.currentPage;
        }
        return page;
    }
    get total() {
        const { dataSet, total } = this.observableProps;
        if (dataSet) {
            return dataSet.totalCount;
        }
        return total;
    }
    get totalPage() {
        const { dataSet } = this.observableProps;
        const { total, pageSize } = this;
        if (dataSet) {
            return dataSet.totalPage;
        }
        if (total !== undefined && pageSize !== undefined) {
            return Math.ceil(total / pageSize);
        }
        return 1;
    }
    get next() {
        const { total = 0, pageSize, page } = this;
        return page < Math.floor((total - 1) / pageSize) + 1;
    }
    getObservableProps(props, context) {
        const globalPagination = this.getContextConfig('pagination');
        const pageSizeOptions = props.pageSizeOptions || (globalPagination && globalPagination.pageSizeOptions) || ['10', '20', '50', '100'];
        const maxPageSize = Math.max(defaultTo('maxPageSize' in props ? props.maxPageSize : (globalPagination && globalPagination.maxPageSize), 100), ...pageSizeOptions);
        return {
            ...super.getObservableProps(props, context),
            page: defaultTo('page' in props ? props.page : globalPagination && globalPagination.page, 1),
            pageSize: defaultTo('pageSize' in props ? props.pageSize : globalPagination && globalPagination.pageSize, 10),
            total: props.total,
            pageSizeOptions,
            pageSizeEditable: 'pageSizeEditable' in props ? props.pageSizeEditable : (globalPagination && globalPagination.pageSizeEditable),
            maxPageSize,
        };
    }
    handlePageSizeBeforeChange(value) {
        if (value < 1 || value > this.observableProps.maxPageSize) {
            return false;
        }
        const { dataSet } = this.props;
        if (dataSet) {
            return dataSet.modifiedCheck();
        }
        return true;
    }
    handlePageSizeChange(value) {
        this.handleChange(this.page, Number(value));
    }
    async handleChange(page, pageSize) {
        const { dataSet, onChange, beforeChange = noop } = this.props;
        if (this.pageSize !== pageSize && await beforeChange(page, pageSize) !== false) {
            runInAction(() => {
                this.observableProps.pageSize = pageSize;
                this.observableProps.page = 1;
                if (dataSet) {
                    dataSet.pageSize = pageSize;
                    dataSet.currentPage = 1;
                    dataSet.query(1, undefined, true);
                }
            });
        }
        else {
            runInAction(() => {
                this.observableProps.page = page;
            });
        }
        if (onChange) {
            onChange(page, pageSize);
        }
    }
    handlePagerClick(page) {
        this.jumpPage(page);
    }
    getValidValue(value) {
        const { page, totalPage } = this;
        value = Number(value);
        if (isNaN(value)) {
            value = page;
        }
        if (value > totalPage) {
            value = totalPage;
        }
        return value;
    }
    async jumpPage(page) {
        const { dataSet, beforeChange = noop } = this.props;
        if (await beforeChange(page, this.pageSize) !== false) {
            if (dataSet) {
                dataSet.page(page);
            }
            this.handleChange(page, this.pageSize);
        }
    }
    handleJumpChange(value) {
        const { page, totalPage, props: { showQuickJumper, simple } } = this;
        value = Number(value);
        if (isNaN(value)) {
            value = page;
        }
        if (value > totalPage) {
            value = totalPage;
        }
        if (simple) {
            this.jumpPage(value);
        }
        else if (showQuickJumper) {
            if (isObject(showQuickJumper) && showQuickJumper.goButton) {
                this.pageInput = value;
            }
            else {
                this.jumpPage(value);
            }
        }
    }
    handleJumpGo(e) {
        if (e.keyCode === KeyCode.ENTER || e.type === 'click') {
            const { pageInput } = this;
            if (pageInput) {
                this.jumpPage(pageInput);
                this.pageInput = '';
            }
        }
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'total',
            'page',
            'pageSize',
            'maxPageSize',
            'pageSizeEditable',
            'onChange',
            'pageSizeOptions',
            'itemRender',
            'showSizeChanger',
            'showQuickJumper',
            'showSizeChangerLabel',
            'showTotal',
            'showPager',
            'sizeChangerPosition',
            'sizeChangerOptionRenderer',
            'hideOnSinglePage',
            'simple',
            'quickJumperPosition',
        ]);
    }
    getOptions() {
        const { Option } = ObserverSelect;
        return this.observableProps.pageSizeOptions.map(option => (React.createElement(Option, { key: option, value: option }, option)));
    }
    getPager(page, type, active = false, disabledSender) {
        const { prefixCls, props: { itemRender = defaultItemRender, disabled = false }, } = this;
        const disabledValue = disabledSender || disabled;
        const classNamePager = isString(type) ? `${prefixCls}-pager-${type}` : ``;
        return (React.createElement(Pager, { key: type === 'page' ? page : type, page: page, active: active, type: type, onClick: this.handlePagerClick, renderer: itemRender, disabled: disabledValue, className: `${prefixCls}-pager ${classNamePager}` }));
    }
    renderPagers(page) {
        const { totalPage } = this;
        const bufferSize = 1;
        const pagerList = [];
        if (totalPage <= 3 + bufferSize * 2) {
            for (let i = 1; i <= totalPage; i += 1) {
                pagerList.push(this.getPager(i, 'page', page === i));
            }
        }
        else {
            let left = Math.max(1, page - bufferSize);
            let right = Math.min(totalPage, page + bufferSize);
            if (page - 1 <= bufferSize) {
                right = 1 + bufferSize * 2;
            }
            if (totalPage - page <= bufferSize) {
                left = totalPage - bufferSize * 2;
            }
            for (let i = left; i <= right; i++) {
                pagerList.push(this.getPager(i, 'page', page === i));
            }
            if (page - 1 >= bufferSize * 2 && page !== 1 + 2) {
                pagerList.unshift(this.getPager(Math.max(page - 5, 1), 'jump-prev'));
            }
            if (totalPage - page >= bufferSize * 2 && page !== totalPage - 2) {
                pagerList.push(this.getPager(Math.min(page + 5, totalPage), 'jump-next'));
            }
            if (left !== 1) {
                pagerList.unshift(this.getPager(1, 'page', page === 1));
            }
            if (totalPage > 1 && right !== totalPage) {
                pagerList.push(this.getPager(totalPage, 'page', page === totalPage));
            }
        }
        return pagerList;
    }
    renderSizeChange(pageSize) {
        const { showSizeChangerLabel, showSizeChanger, sizeChangerOptionRenderer, disabled, } = this.props;
        if (showSizeChanger) {
            const { pageSizeEditable } = this.observableProps;
            const { prefixCls } = this;
            const select = (React.createElement(ObserverSelect, { isFlat: true, searchable: false, key: "size-select", className: classNames(`${prefixCls}-size-changer`, { [`${prefixCls}-size-editable`]: pageSizeEditable }), disabled: disabled, onBeforeChange: this.handlePageSizeBeforeChange, onChange: this.handlePageSizeChange, value: String(pageSize), clearButton: false, renderer: sizeChangerOptionRenderer, optionRenderer: sizeChangerOptionRenderer, combo: pageSizeEditable, restrict: "0-9", size: Size.small }, this.getOptions()));
            return showSizeChangerLabel
                ? [React.createElement("span", { className: `${prefixCls}-perpage`, key: "size-info" }, $l('Pagination', 'records_per_page')), select]
                : select;
        }
    }
    renderTotal(pageSize, page, total) {
        const { prefixCls, props: { showTotal }, dataSet } = this;
        const from = pageSize * (page - 1) + 1;
        const to = Math.min(pageSize * page, total);
        const counting = dataSet && dataSet.counting;
        if (typeof showTotal === 'function') {
            return (React.createElement("span", { key: "total", className: `${prefixCls}-page-info` }, showTotal(total, [from, to], counting !== undefined)));
        }
        return (React.createElement("span", { key: "total", className: `${prefixCls}-page-info` },
            from,
            " - ",
            to,
            " / ",
            counting ? '...' : total));
    }
    /**
     * 渲染快速跳至
     */
    renderQuickGo() {
        const { prefixCls, page } = this;
        const { disabled, showQuickJumper } = this.props;
        let gotoButton = null;
        if (isObject(showQuickJumper) && showQuickJumper.goButton) {
            const { goButton } = showQuickJumper;
            gotoButton =
                typeof goButton === 'boolean' ? (React.createElement(Button, { className: `${prefixCls}-go-button`, onClick: this.handleJumpGo, onKeyUp: this.handleJumpGo, disabled: disabled, size: Size.small }, $l('Pagination', 'jump_to_confirm'))) : (React.createElement("span", { className: `${prefixCls}-go-button`, onClick: this.handleJumpGo, onKeyUp: this.handleJumpGo }, goButton));
        }
        return (React.createElement(QuickJumper, { prefixCls: prefixCls, value: this.pageInput || page, onChange: this.handleJumpChange, disabled: disabled, gotoButton: gotoButton }));
    }
    render() {
        const { total, pageSize, page, props: { hideOnSinglePage, simple }, prefixCls, } = this;
        if (total === undefined || pageSize === undefined || page === undefined) {
            return null;
        }
        if (hideOnSinglePage === true && total <= pageSize) {
            return null;
        }
        const { totalPage, props: { children, sizeChangerPosition, showTotal, showPager, showQuickJumper, quickJumperPosition }, } = this;
        const sizeChanger = this.renderSizeChange(pageSize);
        if (simple) {
            return (React.createElement("nav", Object.assign({}, this.getMergedProps()),
                this.getPager(page - 1, 'prev', false, page === 1),
                React.createElement("li", { className: `${prefixCls}-simple-pager` },
                    React.createElement(ObserverNumberField, { value: page, min: 1, onChange: this.handleJumpChange, valueChangeAction: ValueChangeAction.input, wait: 200 }),
                    React.createElement("span", null, "\uFF0F"),
                    totalPage),
                this.getPager(page + 1, 'next', false, !this.next)));
        }
        return (React.createElement("nav", Object.assign({}, this.getMergedProps()),
            children,
            sizeChangerPosition === SizeChangerPosition.left && sizeChanger,
            showQuickJumper && quickJumperPosition === QuickJumperPosition.left && this.renderQuickGo(),
            showTotal && this.renderTotal(pageSize, page, total),
            this.getPager(1, 'first', false, page === 1),
            this.getPager(page - 1, 'prev', false, page === 1),
            showPager && this.renderPagers(page),
            this.getPager(page + 1, 'next', false, !this.next),
            this.getPager(totalPage, 'last', false, !this.next),
            sizeChangerPosition === SizeChangerPosition.right && sizeChanger,
            showQuickJumper && quickJumperPosition === QuickJumperPosition.right && this.renderQuickGo()));
    }
};
Pagination.displayName = 'Pagination';
Pagination.defaultProps = {
    suffixCls: 'pagination',
    sizeChangerPosition: SizeChangerPosition.left,
    quickJumperPosition: QuickJumperPosition.right,
    sizeChangerOptionRenderer: ({ text }) => text,
    hideOnSinglePage: false,
    showSizeChanger: true,
    showQuickJumper: false,
    showSizeChangerLabel: true,
    showTotal: true,
    simple: false,
};
__decorate([
    observable
], Pagination.prototype, "pageInput", void 0);
__decorate([
    computed
], Pagination.prototype, "pageSize", null);
__decorate([
    computed
], Pagination.prototype, "page", null);
__decorate([
    computed
], Pagination.prototype, "total", null);
__decorate([
    computed
], Pagination.prototype, "totalPage", null);
__decorate([
    computed
], Pagination.prototype, "next", null);
__decorate([
    autobind
], Pagination.prototype, "handlePageSizeBeforeChange", null);
__decorate([
    autobind
], Pagination.prototype, "handlePageSizeChange", null);
__decorate([
    autobind
], Pagination.prototype, "handlePagerClick", null);
__decorate([
    autobind
], Pagination.prototype, "handleJumpChange", null);
__decorate([
    autobind,
    action
], Pagination.prototype, "handleJumpGo", null);
__decorate([
    action
], Pagination.prototype, "getOptions", null);
Pagination = __decorate([
    observer
], Pagination);
export default Pagination;
